import { makeAutoObservable } from "mobx";
import type { EpisodeWithNeighbours, PodcastsApi } from "@/api/podcasts";
import { Resource } from "./Resource";

/** The API pages a podcast's episodes 30 at a time */
const EPISODES_PAGE_SIZE = 30;

interface EpisodesOfPage {
    episodes: Episode[];
    count: number;
    page: number;
}

/**
 * The podcast area: a podcast with its episodes page by page, a single episode, a season, the newest
 * podcasts and the site statistics.
 *
 * Everything about one podcast hangs on `detail`, keyed by slug. The episodes, an episode and a season are
 * resources of their own, keyed by what they are for, and they wait for `detail` to learn the podcast id.
 * Asking for another podcast or page drops the old data at once, and a late answer is ignored.
 */
export class PodcastStore {
    readonly detail: Resource<Podcast, string>;
    readonly episodePage: Resource<EpisodesOfPage, { slug: string; page: number }>;
    readonly episodeDetail: Resource<EpisodeWithNeighbours, { slug: string; episodeSlug: string }>;
    readonly seasonList: Resource<number[], string>;
    readonly seasonPage: Resource<Season, { slug: string; seasonId: number }>;
    readonly siteStats: Resource<SiteStats>;
    readonly latestPodcasts: Resource<Podcast[], { limit: number; lang: string }>;
    /** The podcasts most like a podcast, keyed by its id */
    readonly similarPodcastList: Resource<Podcast[], number>;
    /** The episodes from other podcasts about the same thing as an episode, keyed by its id */
    readonly similarEpisodeList: Resource<Episode[], number>;

    /** Episodes per page, as the API pages them */
    readonly limit = EPISODES_PAGE_SIZE;

    constructor(private readonly api: PodcastsApi) {
        this.detail = new Resource((slug) => this.api.bySlug(slug));
        this.episodePage = new Resource(({ slug, page }) =>
            this.idFor(slug)
                .then((id) => this.api.episodes(id, page))
                .then((result) => ({ ...result, page }))
        );
        this.episodeDetail = new Resource(({ slug, episodeSlug }) =>
            this.idFor(slug).then((id) => this.api.episodeBySlug(id, episodeSlug))
        );
        this.seasonList = new Resource((slug) => this.idFor(slug).then((id) => this.api.seasons(id)));
        this.seasonPage = new Resource(({ slug, seasonId }) =>
            this.idFor(slug).then((id) => this.api.season(id, seasonId))
        );
        this.siteStats = new Resource(() => this.api.stats());
        this.latestPodcasts = new Resource(({ limit, lang }) => this.api.latest(limit, lang));
        this.similarPodcastList = new Resource((podcastId) => this.api.similar(podcastId));
        this.similarEpisodeList = new Resource((episodeId) => this.api.similarEpisodes(episodeId));

        makeAutoObservable<PodcastStore, "api">(this, {
            api: false,
            detail: false,
            episodePage: false,
            episodeDetail: false,
            seasonList: false,
            seasonPage: false,
            siteStats: false,
            latestPodcasts: false,
            similarPodcastList: false,
            similarEpisodeList: false,
            limit: false,
        });
    }

    get podcast(): Podcast | null {
        return this.detail.data ?? null;
    }

    get episodes(): Episode[] {
        return this.episodePage.data?.episodes ?? [];
    }

    /** All the podcast's published episodes, not just the page */
    get count(): number {
        return this.episodePage.data?.count ?? 0;
    }

    /** The page of episodes that is loaded */
    get page(): number {
        return this.episodePage.data?.page ?? 1;
    }

    get loadingEpisodesList() {
        return this.episodePage.loading;
    }

    get episode(): Episode | null {
        return this.episodeDetail.data?.episode ?? null;
    }

    get next(): Episode | null {
        return this.episodeDetail.data?.next ?? null;
    }

    get previous(): Episode | null {
        return this.episodeDetail.data?.previous ?? null;
    }

    /** The numbers of the current podcast's seasons, once loaded */
    get seasons(): number[] {
        return this.seasonList.data ?? [];
    }

    get season(): Season | null {
        return this.seasonPage.data ?? null;
    }

    /** The newest podcasts from the last `loadLatest` */
    get podcasts(): Podcast[] {
        return this.latestPodcasts.data ?? [];
    }

    get stats(): SiteStats | null {
        return this.siteStats.data ?? null;
    }

    get popularPodcasts(): Podcast[] {
        return this.stats?.popular.podcasts.podcasts.slice(0, 5) ?? [];
    }

    get popularEpisodes(): Episode[] {
        return this.stats?.popular.episodes.episodes.slice(0, 5) ?? [];
    }

    /**
     * The podcasts most like podcast `podcastId`, best first. Empty until they have arrived, when there are none,
     * when they could not be loaded, and while the list in the store is for another podcast: a page must never
     * show the neighbours of the podcast it just left.
     */
    similarPodcastsOf(podcastId: number): Podcast[] {
        return this.loadedFor(this.similarPodcastList, podcastId) ?? [];
    }

    /** Like `similarPodcastsOf`, for the episodes about the same thing as episode `episodeId` */
    similarEpisodesOf(episodeId: number): Episode[] {
        return this.loadedFor(this.similarEpisodeList, episodeId) ?? [];
    }

    /**
     * Loads the similar podcasts unless they are loaded. They are an extra: a failure leaves the list empty
     * and never rejects, and the page is not held up by it.
     */
    loadSimilarPodcasts(podcastId: number) {
        return this.similarPodcastList.load(podcastId);
    }

    /** Loads the similar episodes unless they are loaded. Never rejects, like `loadSimilarPodcasts`. */
    loadSimilarEpisodes(episodeId: number) {
        return this.similarEpisodeList.load(episodeId);
    }

    /** True once the podcast and this page of its episodes have arrived */
    isOpen(slug: string, page: number) {
        return (
            this.detail.isFor(slug) &&
            this.detail.loaded &&
            this.episodePage.isFor({ slug, page }) &&
            this.episodePage.loaded
        );
    }

    /**
     * Loads a podcast with a page of its episodes. Settles when both have arrived, so a page that suspends
     * on it never shows half a podcast, and rejects when the podcast itself cannot be loaded.
     */
    async open(slug: string, page = 1) {
        await Promise.all([this.detail.load(slug), this.episodePage.load({ slug, page })]);
        this.throwIfFailed(this.detail);
    }

    isEpisodeOpen(slug: string, episodeSlug: string) {
        return (
            this.detail.isFor(slug) &&
            this.detail.loaded &&
            this.episodeDetail.isFor({ slug, episodeSlug }) &&
            this.episodeDetail.loaded
        );
    }

    /** Loads a podcast with one of its episodes and the episodes on either side of it. Rejects when either is missing. */
    async openEpisode(slug: string, episodeSlug: string) {
        await Promise.all([this.detail.load(slug), this.episodeDetail.load({ slug, episodeSlug })]);
        this.throwIfFailed(this.detail);
        this.throwIfFailed(this.episodeDetail);
    }

    /** Loads the season numbers of a podcast unless they are loaded. The podcast itself is loaded by `open`. */
    loadSeasons(slug: string) {
        return this.seasonList.load(slug);
    }

    isSeasonOpen(slug: string, seasonId: number) {
        return (
            this.detail.isFor(slug) &&
            this.detail.loaded &&
            this.seasonPage.isFor({ slug, seasonId }) &&
            this.seasonPage.loaded
        );
    }

    /** Loads a podcast with one of its seasons. Rejects when either is missing. */
    async openSeason(slug: string, seasonId: number) {
        await Promise.all([this.detail.load(slug), this.seasonPage.load({ slug, seasonId })]);
        this.throwIfFailed(this.detail);
        this.throwIfFailed(this.seasonPage);
    }

    /** Warms the API cache for a podcast page so navigating to it needs no network wait. */
    prefetchPodcastBySlug(slug: string) {
        if (this.podcast?.slug === slug) {
            return;
        }
        this.api
            .bySlug(slug)
            .then((podcast) => this.api.episodes(podcast.id, 1))
            .catch(() => undefined);
    }

    /** Loads the stats unless they are loaded or loading. Failures end up in `siteStats.error`. */
    loadStats() {
        return this.siteStats.load();
    }

    /** True when the list holds the newest `limit` podcasts for `lang` */
    hasLatest(limit: number, lang: string) {
        return this.latestPodcasts.isFor({ limit, lang }) && this.latestPodcasts.loaded;
    }

    /** Loads the newest podcasts. Rejects when that fails. */
    async loadLatest(limit = 30, lang = "sv") {
        await this.latestPodcasts.load({ limit, lang });
        this.throwIfFailed(this.latestPodcasts);
    }

    // Episodes, episode and season are asked for by podcast id, which only the podcast itself knows
    private async idFor(slug: string): Promise<number> {
        await (this.detail.isFor(slug) ? this.detail.whenSettled() : this.detail.load(slug));
        if (!this.detail.isFor(slug) || !this.detail.data) {
            throw this.detail.error ?? new Error(`Podcast not found: ${slug}`);
        }
        return this.detail.data.id;
    }

    /**
     * What a list resource holds for `key`, once it has arrived. The status is read first on purpose: it is
     * observable, and a view that calls this before its load has started would otherwise subscribe to nothing
     * and never hear that the list has arrived.
     */
    private loadedFor<T>(resource: Resource<T, number>, key: number): T | undefined {
        if (resource.status !== "success" || !resource.isFor(key)) {
            return undefined;
        }
        return resource.data;
    }

    private throwIfFailed(resource: { status: string; error: unknown }) {
        if (resource.status === "error") {
            throw resource.error;
        }
    }
}
